import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ScanFace, Loader2, User, ArrowRight, X, AlertCircle, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FaceMatchPage = () => {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState('');
  const [totalCompared, setTotalCompared] = useState(0);
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file); setMatches([]); setError('');
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSearch = async () => {
    if (!image) return;
    setLoading(true); setScanning(true); setError(''); setMatches([]);
    try {
      const formData = new FormData();
      formData.append('image', image);
      await new Promise(resolve => setTimeout(resolve, 2000));
      setScanning(false);
      const response = await axios.post(`${API}/api/face-match`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMatches(response.data.matches || []);
      setTotalCompared(response.data.total_compared || 0);
    } catch (err) {
      console.error('Face match error:', err);
      setError(err.response?.data?.detail || 'चेहरा मिलान में त्रुटि हुई। कृपया पुनः प्रयास करें।');
    } finally { setLoading(false); setScanning(false); }
  };

  const clearImage = () => {
    setImage(null); setImagePreview(null); setMatches([]); setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getPhotoUrl = (path) => path ? (path.startsWith('http') ? path : `${API}/api/files/${path}`) : null;

  return (
    <div className="pb-24 md:pb-12" data-testid="face-match-page">
      {/* ===== MOBILE HEADER ===== */}
      <div className="md:hidden bg-gradient-to-br from-violet-600 to-indigo-600 text-white p-6 pb-8 rounded-b-[2rem]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center"><ScanFace className="w-6 h-6" /></div>
          <div>
            <h1 className="font-outfit font-bold text-xl">AI चेहरा मिलान</h1>
            <p className="text-white/70 text-sm">फोटो से मिलते-जुलते चेहरे खोजें</p>
          </div>
        </div>
        <div className="flex items-start gap-2 p-3 bg-white/10 rounded-xl text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="text-white/80 text-xs">AI-संचालित मिलान। परिणाम 100% सटीक नहीं हो सकते।</p>
        </div>
      </div>

      {/* ===== DESKTOP HEADER ===== */}
      <div className="hidden md:block py-8 border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center">
              <ScanFace className="w-7 h-7 text-violet-600" />
            </div>
            <div>
              <h1 className="font-outfit font-bold text-3xl text-gray-900">AI चेहरा मिलान</h1>
              <p className="text-gray-500">फोटो अपलोड करें और डेटाबेस में मिलते-जुलते चेहरों की खोज करें</p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="px-4 md:px-0 md:py-8 -mt-4 md:mt-0">
        <div className="md:max-w-4xl md:mx-auto md:px-6">
          <div className="md:grid md:grid-cols-2 md:gap-8">
            {/* Upload Section */}
            <div>
              <div className="app-card p-6">
                <h3 className="hidden md:block font-outfit font-semibold text-lg text-gray-900 mb-4">फोटो अपलोड करें</h3>
                <label className="block cursor-pointer">
                  <div className={`aspect-square max-w-[240px] mx-auto rounded-2xl overflow-hidden border-2 border-dashed ${imagePreview ? 'border-violet-400' : 'border-gray-300'} flex items-center justify-center bg-gray-50 relative`}>
                    {imagePreview ? (
                      <>
                        <img src={imagePreview} alt="Upload preview" className="w-full h-full object-cover" />
                        {scanning && (
                          <div className="absolute inset-0 overflow-hidden">
                            <div className="absolute inset-x-0 h-1 bg-violet-500 animate-scan-line" />
                            <div className="absolute inset-0 bg-violet-500/20" />
                          </div>
                        )}
                        <button onClick={(e) => { e.preventDefault(); clearImage(); }} className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-lg" data-testid="clear-image">
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <div className="text-center p-6">
                        <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 font-medium">फोटो अपलोड करें</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG (5MB तक)</p>
                      </div>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" data-testid="upload-input" />
                </label>

                <Button onClick={handleSearch} disabled={!image || loading} className="w-full mt-6 h-14 rounded-full bg-violet-600 hover:bg-violet-700" data-testid="search-face-btn">
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{scanning ? 'स्कैन हो रहा है...' : 'खोज रहा है...'}</>
                  ) : (
                    <><ScanFace className="w-5 h-5 mr-2" />चेहरा खोजें</>
                  )}
                </Button>

                {/* Desktop Disclaimer */}
                <div className="hidden md:flex items-start gap-2 mt-4 p-3 bg-violet-50 rounded-xl text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-violet-500" />
                  <p className="text-violet-700 text-xs">AI-संचालित मिलान। परिणाम 100% सटीक नहीं हो सकते। व्यक्तिगत रूप से पुष्टि करें।</p>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div>
              {error && <div className="app-card p-4 bg-red-50 border-red-200 text-red-700 text-sm mb-4">{error}</div>}

              {!image && !loading && matches.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><ScanFace className="w-10 h-10 text-gray-300" /></div>
                  <p className="text-gray-500 font-medium">खोजने के लिए फोटो अपलोड करें</p>
                </div>
              )}

              {loading && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">AI डेटाबेस में चेहरों की तुलना कर रहा है...</p>
                </div>
              )}

              {!loading && matches.length === 0 && image && !error && (
                <div className="text-center py-12 app-card">
                  <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">कोई मिलता-जुलता चेहरा नहीं मिला</p>
                  <p className="text-sm text-gray-400 mt-2">हमारे डेटाबेस में इस चेहरे से मिलता कोई रिकॉर्ड नहीं है</p>
                </div>
              )}

              {matches.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-4">{totalCompared} फोटो में से <span className="font-semibold text-gray-900">{matches.length}</span> मिलान मिले</p>
                  <div className="space-y-4">
                    {matches.map((match, index) => (
                      <motion.div key={match.case_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} data-testid={`match-result-${match.case_id}`}>
                        <Link to={`/case/${match.case_id}`}>
                          <div className="app-card overflow-hidden card-hover">
                            <div className="flex gap-4 p-4">
                              <div className="w-20 h-20 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                                {match.photo_url ? <img src={getPhotoUrl(match.photo_url)} alt={match.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-8 h-8 text-gray-400" /></div>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <div><h4 className="font-semibold text-gray-900 truncate">{match.name}</h4><p className="text-sm text-gray-500">{match.city}, {match.state}</p></div>
                                  <span className={`px-2 py-1 text-xs font-bold rounded-full ${match.case_type === 'missing' ? 'badge-missing' : 'badge-unknown'}`}>{match.case_type === 'missing' ? 'लापता' : 'अज्ञात'}</span>
                                </div>
                                <div className="mt-3">
                                  <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="text-gray-500">समानता</span>
                                    <span className={`font-bold ${match.similarity_score >= 70 ? 'text-green-600' : match.similarity_score >= 50 ? 'text-amber-600' : 'text-gray-600'}`}>{match.similarity_score}%</span>
                                  </div>
                                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${match.similarity_score >= 70 ? 'bg-green-500' : match.similarity_score >= 50 ? 'bg-amber-500' : 'bg-gray-400'}`} style={{ width: `${match.similarity_score}%` }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-center gap-2 py-3 bg-gray-50 text-gray-600 text-sm font-medium border-t border-gray-100">विस्तृत जानकारी<ArrowRight className="w-4 h-4" /></div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaceMatchPage;
